import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { createFileRoute, useParams } from '@tanstack/react-router'
import $type from '@tandem/ui/type.css'
import $table from '@tandem/ui/table.css'
import Spinner from '@tandem/ui/Spinner'
import $ from './-ImportPage.css'
import { useTRPC } from '@/trpc'

export const Route = createFileRoute('/catalog/import/$id')({
  component: ImportDetailsPage,
})

function ImportDetailsPage() {
  let { id } = useParams({ from: '/catalog/import/$id' })
  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let queryKey = trpc.import.get.queryOptions({ id: Number(id) }).queryKey
  let { data } = useQuery({
    ...trpc.import.get.queryOptions({ id: Number(id) }),
    refetchInterval: (query) => {
      let d = query.state.data
      return d && d.processing > 0 ? 15_000 : false
    },
  })

  let restart = useMutation({
    ...trpc.import.restart.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  let [selected, setSelected] = useState<Set<number>>(new Set())

  let debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  let isComplete = !data || data.processing === 0

  useEffect(() => {
    if (isComplete) return

    let es = new EventSource(`${env.API_URL}/sse/import/${id}`)

    es.onmessage = (e) => {
      let payload = JSON.parse(e.data)
      let done = payload.status === 'completed'

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old
        return { ...old, processing: done ? 0 : 1 }
      })

      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey })
      }, 500)
    }

    return () => {
      es.close()
      clearTimeout(debounceRef.current)
    }
  }, [id, isComplete])

  if (!data) return null

  let allIds = data.products.map((p) => p.import_product_id)
  let allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id))

  function toggleAll() {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(allIds))
    }
  }

  function toggle(rowId: number) {
    setSelected((prev) => {
      let next = new Set(prev)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  }

  function handleRestart() {
    let rows = selected.size > 0 ? [...selected] : undefined
    restart.mutate({ id: Number(id), rows })
    setSelected(new Set())
  }

  let statusLabel: Record<string, string> = {
    pending: 'Ожидает',
    processing: 'Обработка...',
    done: 'Готово',
    error: 'Ошибка',
  }

  let statusColor: Record<string, string> = {
    pending: '#888',
    processing: '#e6a200',
    done: '#2a7d2a',
    error: '#c33',
  }

  return (
    <div>
      <div class={clsx($type.breadcrumb, $type.small)}>
        <a href='/catalog' class={$type.underline}>
          Каталог
        </a>
        <span>/</span>
        <a href='/catalog/import' class={$type.underline}>
          Импорт
        </a>
        <span>/</span>
        <b>Выгрузка #{data.id}</b>
      </div>

      <h2>{data.original_filename || `Выгрузка #${data.id}`}</h2>

      <p class={$type.small} style={{ color: '#888' }}>
        Загружена {new Date(data.created_at).toLocaleString('ru')}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1em', marginTop: '0.5em' }}>
        <p>
          Всего в выгрузке <b>{data.total}</b> товаров. Обработано <b>{data.processed}</b>.
        </p>
        <button
          class='secondary'
          disabled={restart.isPending}
          onClick={handleRestart}
        >
          {restart.isPending
            ? <Spinner />
            : selected.size > 0
              ? `Перезапустить (${selected.size})`
              : 'Перезапустить все'}
        </button>
      </div>

      {data.products.length > 0 ? (
        <div class={$.tableContainer} style={{ overflowX: 'auto', marginTop: '1em' }}>
        <table class={clsx($.table, $table.table, $table.condensed)}>
          <thead>
            <tr>
              <th>
                <input
                  type='checkbox'
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th></th>
              <th>Статус</th>
              <th>Артикул</th>
              <th>Наименование</th>
              <th>Бренд</th>
              <th>Категория</th>
              {(data.attributes ?? []).map((attr) => (
                <th key={attr.id}>{attr.name}{attr.unit ? `, ${attr.unit}` : ''}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.products.map((product) => {
              let raw = product.raw as Record<string, string> | null
              let name = product.name || (raw && Object.values(raw).find((v) => v)) || '—'
              let article = product.article_number || '—'

              return (
                <tr key={product.import_product_id} style={{ opacity: product.status === 'pending' ? 0.6 : 1 }}>
                  <td>
                    <input
                      type='checkbox'
                      checked={selected.has(product.import_product_id)}
                      onChange={() => toggle(product.import_product_id)}
                    />
                  </td>
                  <td>
                    {product.image_url
                      ? <img src={product.image_url} width={32} height={32} style={{ objectFit: 'cover', borderRadius: 4, display: 'block', border: '1px solid #ddd' }} />
                      : null}
                  </td>
                  <td>
                    <span style={{ color: statusColor[product.status] || '#888', fontSize: '0.85em' }}>
                      {product.status === 'processing' ? <Spinner /> : statusLabel[product.status] || product.status}
                    </span>
                  </td>
                  <td>{article}</td>
                  <td>{name}</td>
                  <td>{product.brand_name || '—'}</td>
                  <td>{product.category_name || '—'}</td>
                  {(data.attributes ?? []).map((attr) => {
                    let val = (product.attribute_values ?? {})[attr.id]
                    return <td key={attr.id}>{val != null ? String(val) : '—'}</td>
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      ) : (
        <p class={$type.muted} style={{ marginTop: '1em' }}>
          {data.total > 0 ? 'Обработка...' : 'Нет товаров в выгрузке'}
        </p>
      )}
    </div>
  )
}
